// The only place that talks HTTP (CODING-RULES §4.2). Pages call the typed functions in the area modules.

import { apiBaseUrl } from "../config";
import { getToken } from "../session";
import type { Problem } from "./types";

export type ApiResult<T> = { ok: true; data: T } | { ok: false; problem: Problem };
type Listener = () => void;

const NETWORK_ERROR_CODE: string = "NETWORK";
let unauthorizedListener: Listener = (): void => undefined;
let mutationListener: Listener = (): void => undefined;

/** Called on any 401: the app sends the user to sign-in and back afterwards (SPEC §7.1). */
export function onUnauthorized(listener: Listener): void {
  unauthorizedListener = listener;
}

/** Called after every successful change, e.g. to refresh the bell count (SPEC §7.10). */
export function onMutation(listener: Listener): void {
  mutationListener = listener;
}

export async function apiGet<T>(path: string): Promise<ApiResult<T>> {
  return request<T>("GET", path, undefined);
}

export async function apiPost<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>("POST", path, body);
}

export async function apiPut<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>("PUT", path, body);
}

export async function apiDelete<T>(path: string): Promise<ApiResult<T>> {
  return request<T>("DELETE", path, undefined);
}

/** Builds "?a=1&b=2" from defined values only. */
export function queryString(values: Record<string, string | number | boolean | null | undefined>): string {
  const search: URLSearchParams = new URLSearchParams();
  for (const [name, value] of Object.entries(values)) {
    if (value !== null && value !== undefined) {
      search.set(name, String(value));
    }
  }

  const text: string = search.toString();
  return text === "" ? "" : `?${text}`;
}

async function request<T>(method: string, path: string, body: unknown): Promise<ApiResult<T>> {
  const sentToken: boolean = getToken() !== null;
  let response: Response;
  try {
    response = await fetch(apiBaseUrl() + path, buildInit(method, body));
  } catch {
    return { ok: false, problem: buildProblem(0, NETWORK_ERROR_CODE) };
  }

  if (response.ok) {
    return readSuccess<T>(method, response);
  }

  // A 401 means the session ended only when a session token was sent; a failed sign-in is answered on its own page.
  if (response.status === 401 && sentToken) {
    unauthorizedListener();
  }

  return { ok: false, problem: await readProblem(response) };
}

function buildInit(method: string, body: unknown): RequestInit {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token: string | null = getToken();
  if (token !== null) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (body === undefined) {
    return { method, headers };
  }

  headers["Content-Type"] = "application/json";
  return { method, headers, body: JSON.stringify(body) };
}

async function readSuccess<T>(method: string, response: Response): Promise<ApiResult<T>> {
  if (method !== "GET") {
    mutationListener();
  }

  const text: string = await response.text();
  const data: unknown = text === "" ? null : JSON.parse(text);
  return { ok: true, data: data as T };
}

async function readProblem(response: Response): Promise<Problem> {
  try {
    const body: unknown = await response.json();
    return { ...buildProblem(response.status, "INTERNAL_ERROR"), ...(body as Partial<Problem>) };
  } catch {
    return buildProblem(response.status, "INTERNAL_ERROR");
  }
}

function buildProblem(status: number, code: string): Problem {
  return { status, code, params: {}, fieldErrors: [], conflicts: [], current: null };
}
